// SessionsVsScoreScatter.jsx
import React, { useEffect, useMemo } from "react";
import {
  Box,
  Stack,
  Typography,
  Divider,
  Paper,
  useTheme,
} from "@mui/material";
import { TrendingUpSharp } from "@mui/icons-material";
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ZAxis,
} from "recharts";
import ScatterPlotIcon from '@mui/icons-material/ScatterPlot';
const CustomTooltip = ({ active, payload }) => {
  const theme = useTheme();
  if (!active || !payload || !payload.length) return null;
  const p = payload[0]?.payload ?? {};
  return (
    <Paper
      elevation={6}
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        minWidth: 200,
      }}
    >
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        {p.name ?? `Student #${p.student_id}`}
      </Typography>
      <Typography variant="body2"><strong>Sessions:</strong> {p.x}</Typography>
      <Typography variant="body2"><strong>Avg. Score:</strong> {p.y}%</Typography>
    </Paper>
  );
};

export default function SessionsVsScoreScatterPlot({ data }) {


  const rows = Array.isArray(data) ? data : [];
  const theme = useTheme();
  const points = useMemo(() => {
    const rows = Array.isArray(data) ? data : [];
    return rows.map((r) => ({
      x: Number(r.session_count) || 0,
      y: Number(r.score_average) || 0,
      student_id: r.student_id,
      name: `${(r.first_name || "").trim()} ${(r.last_name || "").trim()}`.trim(),
    }));
  }, [rows]);

  const xMax = useMemo(
    () => Math.max(0, ...points.map((p) => p.x)),
    [points]
  );
  const yMax = useMemo(
    () => Math.max(0, ...points.map((p) => p.y)),
    [points]
  );

  return (
    <Box sx={{ p: 1 }}>
      {/* Header (kept the same as your snippet) */}
      <Box sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1} sx={{ display: "flex", alignItems: "center" }}>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Sessions and average score
            </Typography>
            <Typography variant="body2" color="text.secondary">
              &nbsp;Number of session compared with average scores
            </Typography>
          </Box>
        </Stack>
        <Divider />
      </Box>

      {/* Empty state */}
      {!points.length ? (
        <Paper
          variant="outlined"
          sx={{
            height: 350,
            borderRadius: 2,
            display: "grid",
            placeItems: "center",
            color: "text.secondary",
          }}
        >
          <Typography variant="body2">No data to display</Typography>
        </Paper>
      ) : (
        <Box sx={{ width: "100%", height: 400 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 30, right: 20, left: 20, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                type="number"
                dataKey="x"
                name="Sessions"
                label={{ value: "Sessions", position: "insideBottom", fill: theme.palette.text.primary }}
                domain={[0, Math.max(5, xMax + 1)]}
                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
              />
              <YAxis
                type="number"
                dataKey="y"
                name="Average Score (%)"
                label={{ value: "Avg Score (%)", angle: -90, position: "insideLeft", fill: theme.palette.text.primary }}
                domain={[0, Math.max(100, Math.ceil(yMax / 10) * 10)]}
                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
              />
              {/* Optional ZAxis if you later want to size by something (e.g., number of assessments): */}
              <ZAxis range={[60, 120]} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Scatter
                name="Students"
                data={points}
                fill={theme.palette.secondary.main}
                // Draw small circles; can set shape="circle"
              />
            </ScatterChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
}